import React, { useState } from 'react';
import { Box, Typography, Alert, InputBase } from '@mui/material';
import { Formik, Form } from 'formik';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import formatErrorToString from '../../helpers/formatErrorToString';
import getFirstAccessibleRoute from '../../helpers/getFirstAccessibleRoute';
import { useTranslation } from 'react-i18next';
import { useLogin } from '@openapi/auth/auth';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import KeyIcon from '@mui/icons-material/Key';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import TikiLayout, { TikiInput, TikiButton, tiki } from './TikiLayout';

function TikiLoginPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, setUser, setAccessToken } = useAuth();
    const loginMutation = useLogin();
    const [twoFactorState, setTwoFactorState] = useState(null);
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState('');

    function handleSuccess(data) {
        setAccessToken(data.token);
        setUser(data.user);
    }

    async function handleVerify() {
        if (code.length !== 6) return;
        setCodeError('');
        try {
            const data = await loginMutation.mutateAsync({
                data: {
                    email: twoFactorState.email,
                    password: twoFactorState.password,
                    mfa_token: code,
                },
            });
            handleSuccess(data);
        } catch (_error) {
            setCodeError(t('login.tiki.twoFactorError'));
            setCode('');
        }
    }

    if (user) return <Navigate to={getFirstAccessibleRoute(user)} replace />;

    if (twoFactorState) {
        return (
            <TikiLayout showFooterIcons>
                <Box sx={{ mb: 5, textAlign: 'left' }}>
                    <Typography
                        variant="h1"
                        sx={{
                            fontFamily: tiki.headline,
                            fontWeight: 800,
                            fontSize: '2.25rem',
                            color: tiki.colors.surface,
                            mb: 1.5,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {t('login.tiki.twoFactorTitle')}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: tiki.body,
                            fontSize: '1.125rem',
                            color: tiki.colors.textMuted,
                        }}
                    >
                        {t('login.tiki.twoFactorDescription')}
                    </Typography>
                </Box>

                {codeError && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '0.75rem' }}>
                        {codeError}
                    </Alert>
                )}

                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, mb: 1 }}>
                        <Typography
                            component="label"
                            htmlFor="tiki-2fa-code"
                            sx={{
                                fontFamily: tiki.headline,
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                color: tiki.colors.text,
                            }}
                        >
                            {t('login.tiki.twoFactorLabel')}
                        </Typography>
                    </Box>
                    <Box sx={{ position: 'relative' }}>
                        <LockIcon
                            sx={{
                                position: 'absolute',
                                left: 16,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: tiki.colors.textFaint,
                                fontSize: 22,
                            }}
                        />
                        <InputBase
                            id="tiki-2fa-code"
                            autoFocus
                            placeholder="000000"
                            value={code}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                if (val.length <= 6) setCode(val);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleVerify();
                            }}
                            inputProps={{ maxLength: 6 }}
                            sx={{
                                width: '100%',
                                pl: 6,
                                pr: 2,
                                py: 1.75,
                                fontFamily: tiki.body,
                                fontSize: '1.5rem',
                                letterSpacing: '0.5rem',
                                fontWeight: 700,
                                color: tiki.colors.surface,
                                bgcolor: tiki.colors.inputBg,
                                border: `1px solid ${tiki.colors.inputBorder}`,
                                borderRadius: '0.75rem',
                                transition: 'all 0.3s',
                                '&.Mui-focused': {
                                    outline: `2px solid ${tiki.colors.primary}`,
                                    borderColor: 'transparent',
                                },
                                '& input::placeholder': {
                                    color: tiki.colors.textGhost,
                                    opacity: 1,
                                    letterSpacing: '0.5rem',
                                },
                            }}
                        />
                    </Box>
                </Box>

                <TikiButton
                    disabled={code.length !== 6 || loginMutation.isPending}
                    onClick={handleVerify}
                    type="button"
                >
                    {loginMutation.isPending
                        ? t('login.tiki.twoFactorLoading')
                        : t('login.tiki.twoFactorSubmit')}
                    {!loginMutation.isPending && <ArrowForwardIcon sx={{ fontSize: 20 }} />}
                </TikiButton>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography
                        component="a"
                        onClick={() => {
                            setTwoFactorState(null);
                            setCode('');
                            setCodeError('');
                        }}
                        sx={{
                            fontFamily: tiki.body,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: tiki.colors.textMuted,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': { color: tiki.colors.surface },
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: 16 }} />
                        {t('common.back')}
                    </Typography>
                </Box>
            </TikiLayout>
        );
    }

    return (
        <TikiLayout showFooterIcons>
            <Box sx={{ mb: 5, textAlign: 'left' }}>
                <Typography
                    variant="h1"
                    sx={{
                        fontFamily: tiki.headline,
                        fontWeight: 800,
                        fontSize: '2.25rem',
                        color: tiki.colors.surface,
                        mb: 1.5,
                        letterSpacing: '-0.02em',
                    }}
                >
                    Aloha Admin!
                </Typography>
                <Typography
                    sx={{
                        fontFamily: tiki.body,
                        fontSize: '1.125rem',
                        color: tiki.colors.textMuted,
                    }}
                >
                    Ignite your dashboard to manage tonight&apos;s performance.
                </Typography>
            </Box>

            <Formik
                initialValues={{ email: '', password: '' }}
                onSubmit={async ({ email, password }, { setSubmitting, setErrors }) => {
                    setErrors({});
                    loginMutation.reset();
                    try {
                        const data = await loginMutation.mutateAsync({ data: { email, password } });
                        if (!data?.user) {
                            setErrors({ api: data?.message || t('common.error') });
                            return;
                        }
                        handleSuccess(data);
                    } catch (error) {
                        if (formatErrorToString(error) === 'mfa_required') {
                            setTwoFactorState({ email, password });
                            return;
                        }
                        setErrors({ api: formatErrorToString(error) });
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ errors }) => (
                    <Form>
                        {errors?.api && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: '0.75rem' }}>
                                {errors.api}
                            </Alert>
                        )}

                        <TikiInput
                            name="email"
                            label="Island ID"
                            type="email"
                            placeholder="performer_name_01"
                            icon={<PersonOutlineIcon />}
                        />

                        <TikiInput
                            name="password"
                            label="Secret Handshake"
                            type="password"
                            placeholder="••••••••"
                            icon={<KeyIcon />}
                            right={
                                <Typography
                                    component="a"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate('/forgotpassword?theme=tiki');
                                    }}
                                    sx={{
                                        fontFamily: tiki.body,
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: tiki.colors.secondary,
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    Forgot?
                                </Typography>
                            }
                        />

                        <TikiButton disabled={loginMutation.isPending}>
                            {loginMutation.isPending ? 'Loading...' : 'Join the Luau'}
                            {!loginMutation.isPending && <ArrowForwardIcon sx={{ fontSize: 20 }} />}
                        </TikiButton>
                    </Form>
                )}
            </Formik>
        </TikiLayout>
    );
}

export default TikiLoginPage;
